import { html, nothing, TemplateResult } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';

import { OutlineElement } from '../../base/outline-element/outline-element';
import '../../base/outline-container/outline-container';
import '../ghs-icon/ghs-icon';
import '../../base/outline-modal/outline-modal';
import '../ghs-video-youtube/ghs-video-youtube';
import '../ghs-button/ghs-button';

export type VideoVariantType = 'left' | 'right';

import componentStyles from './ghs-full-video-cta.css.lit';
import { GhsVideoYoutube } from '../ghs-video-youtube/ghs-video-youtube';
import { ifDefined } from 'lit/directives/if-defined.js';

/**
 * The video component.
 * @element ghs-full-video-cta
 * @slot - The default slot.
 */
@customElement('ghs-full-video-cta')
export class GhsFullVideoCta extends OutlineElement {
  static styles = [componentStyles];

  @state() isVisible = false;

  @state() isLoading = true;

  /**
   * The heading text.
   */
  @property({ type: String }) heading: string;

  @property() playerIcon: string;

  /**
   * CTA link caption.
   */
  @property({ type: String, attribute: 'cta-text', reflect: true })
  ctaText = '';

  /**
   * The Background image URL.
   */
  @property({ attribute: 'bg-image-url' })
  bgImageUrl: String;

  @property({ type: String, attribute: 'video-id', reflect: true })
  videoID: string;

  @property({ type: String, attribute: 'video-url', reflect: true })
  videoURL: string;

  @property() variant: VideoVariantType = 'right';

  /**
   * Video youtube selector.
   */
  @query('ghs-video-youtube')
  videoYoutubeEl: GhsVideoYoutube;

  @query('video')
  videoLocalEl: HTMLVideoElement;

  @state() hasImgSlot: boolean;

  firstUpdated(): void {
    super.firstUpdated();
    this.hasImgSlot = this.querySelector('[slot="image"]') !== null;
    const intervalId = setInterval(() => {
      if (this.videoYoutubeEl?.player) {
        clearInterval(intervalId);

        // Update is-loading state variable.
        this.isLoading = false;
      }
    }, 500);

    if (this.videoURL) {
      this.isLoading = false;
    }
  }

  handleClick = () => {
    this.isVisible = true;

    if (this.videoYoutubeEl) {
      this.videoYoutubeEl.player?.playVideo();
    }

    if (this.videoLocalEl) {
      this.videoLocalEl.play();
    }
  };

  renderRemoteVideo(): TemplateResult {
    const videoClasses = {
      'video': true,
      'video--visible': this.isVisible,
    };

    return html`
      <div class=${classMap(videoClasses)}>
        <ghs-video-youtube
          video-id=${this.videoID}
          site=${this.site}
        ></ghs-video-youtube>
      </div>
    `;
  }

  renderLocalVideo(): TemplateResult {
    const videoClasses = {
      'video': true,
      'video--visible': this.isVisible,
    };

    return html`
      <div class=${classMap(videoClasses)}>
        <video controls width="100%" height="100%">
          <source src="${this.videoURL}" type="video/mp4" />
        </video>
      </div>
    `;
  }

  renderImage(): TemplateResult {
    return html`
      <div class="image-wrapper">
        <slot name="image"></slot>
      </div>
    `;
  }

  render(): TemplateResult {
    if (this.site == 'BH') {
      this.playerIcon = this.variant == 'left' ? 'player-left' : 'player-right';
    } else {
      this.playerIcon = 'player';
    }
    return html`
      <div
        class="full-video-cta ${this.variant}"
        style="${ifDefined(
          this.site !== 'BH'
            ? `background-image: url(${this.bgImageUrl});`
            : undefined
        )}"
      >
        ${this.hasImgSlot ? this.renderImage() : nothing}
        <div class="overlay">
          <outline-container>
            <div class="container">
              <button
                class="player"
                title="${this.ctaText}"
                aria-label="Play video"
                @click=${this.handleClick}
                ?disabled=${this.isLoading}
              >
                <ghs-icon
                  class="desktop-icon"
                  name=${this.playerIcon}
                  size=${this.site === 'BH' ? '350px' : '60px'}
                  mobile-size="20px"
                ></ghs-icon>

                ${
                  // Only bellin has a different icon for mobile.
                  this.site == 'BH'
                    ? html`
                        <ghs-icon
                          class="mobile-icon"
                          name="player-mobile"
                          size="78px"
                          mobile-size="78px"
                        ></ghs-icon>
                      `
                    : nothing
                }
              </button>

              <div class="content">
                ${this.heading
                  ? html`<h2 class="heading">${this.heading}</h2>`
                  : nothing}
                ${this.ctaText
                  ? html`
                      <div class="cta-btn">
                        <ghs-button
                          @click=${this.handleClick}
                          variant="tertiary"
                          icon="chevron-right"
                          icon-position="right"
                        >
                          ${this.ctaText}
                        </ghs-button>
                      </div>
                    `
                  : nothing}
              </div>
            </div>
          </outline-container>
        </div>

        ${this.videoID ? this.renderRemoteVideo() : nothing}
        ${this.videoURL ? this.renderLocalVideo() : nothing}
      </div>
    `;
  }
}
